import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { X, Cookie, ChevronDown, ChevronUp } from 'lucide-react';
import { CookiePreferences } from '@/hooks/useCookieConsent';
import { cn } from '@/lib/utils';

interface CookieConsentBannerProps {
  onAcceptAll: () => void;
  onRejectAll: () => void;
  onSavePreferences: (preferences: CookiePreferences) => void;
  initialPreferences: CookiePreferences;
}

export const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({
  onAcceptAll,
  onRejectAll,
  onSavePreferences,
  initialPreferences,
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(initialPreferences);

  const handlePreferenceChange = (key: keyof CookiePreferences, value: boolean) => {
    if (key === 'necessary') return; // Cannot disable necessary cookies
    setPreferences(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    onSavePreferences(preferences);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-in slide-in-from-bottom-5 duration-300">
      <div className="mx-auto max-w-4xl rounded-2xl bg-card border border-border shadow-xl overflow-hidden">
        <div className="p-5">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Cookie className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-foreground mb-1">
                We value your privacy 🍪
              </h3>
              <p className="text-sm text-muted-foreground">
                We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
                You can choose which cookies to accept.
              </p>
            </div>
          </div>

          {/* Expandable Details */}
          <div className={cn(
            "mt-4 overflow-hidden transition-all duration-300",
            showDetails ? "max-h-96" : "max-h-0"
          )}>
            <div className="space-y-4 pt-4 border-t border-border">
              {/* Necessary Cookies */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-foreground">Necessary Cookies</p>
                  <p className="text-xs text-muted-foreground">
                    Essential for the website to function properly. Cannot be disabled.
                  </p>
                </div>
                <Switch checked={true} disabled className="opacity-50" />
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-foreground">Analytics Cookies</p>
                  <p className="text-xs text-muted-foreground">
                    Help us understand how visitors interact with our website (Google Analytics, Ahrefs).
                  </p>
                </div>
                <Switch
                  checked={preferences.analytics}
                  onCheckedChange={(checked) => handlePreferenceChange('analytics', checked)}
                />
              </div>

              {/* Marketing Cookies */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-foreground">Marketing Cookies</p>
                  <p className="text-xs text-muted-foreground">
                    Used to track visitors across websites for advertising purposes.
                  </p>
                </div>
                <Switch
                  checked={preferences.marketing}
                  onCheckedChange={(checked) => handlePreferenceChange('marketing', checked)}
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-2 flex flex-col sm:flex-row items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDetails(!showDetails)}
              className="text-muted-foreground hover:text-foreground order-last sm:order-first"
            >
              {showDetails ? (
                <>
                  <ChevronUp className="w-4 h-4 mr-1" />
                  Hide details
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4 mr-1" />
                  Customize
                </>
              )}
            </Button>
            
            <div className="flex-1" />
            
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                onClick={onRejectAll}
                className="flex-1 sm:flex-initial"
              >
                Reject All
              </Button>
              {showDetails ? (
                <Button
                  onClick={handleSave}
                  className="flex-1 sm:flex-initial text-white"
                >
                  Save Preferences
                </Button>
              ) : (
                <Button
                  onClick={onAcceptAll}
                  className="flex-1 sm:flex-initial text-white"
                >
                  Accept All
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Privacy Policy Link */}
        <div className="px-5 py-3 bg-muted/50 border-t border-border">
          <p className="text-xs text-muted-foreground text-center">
            By using our site, you agree to our{' '}
            <a href="#/privacy-policy" className="text-primary hover:underline">
              Privacy Policy
            </a>
            . You can change your preferences at any time.
          </p>
        </div>
      </div>
    </div>
  );
};
